<template>
    <div class="mx-auto p-4 h-screen flex flex-col relative">
        <h2 class="text-2xl font-black font-lato mb-4 text-white inline-flex items-center">
            Manage Fish
        </h2>
        <button @click="showAddFishModal = true" class="absolute top-4 right-4 bg-primary hover:bg-blue-600 text-white px-4 py-2 rounded-lg shadow-md transition duration-300 ease-in-out flex items-center justify-center">
            <span class="mr-2">Add New</span>
            <i class="mdi mdi-plus"></i>
        </button>
        <div v-if="fishList.length > 0" class="grid grid-cols-1 md:grid-cols-2 gap-4 flex-grow overflow-hidden">
            <div class="bg-black p-4 rounded-lg overflow-auto no-scrollbar">
                <div v-for="(fishes, group) in groupedFishList" :key="group">
                    <h4 class="text-lg font-semibold text-white mb-2 inline-flex items-center">
                        {{ group }}
                        <span class="ml-2 px-2 py-1 text-xs bg-neutral-700 text-white rounded-full">
                            {{ fishes.length }}
                        </span>
                    </h4>
                    <ul class="mb-4">
                        <li v-for="fish in fishes" :key="fish.id" 
                            @click="selectFish(fish)"
                            class="cursor-pointer hover:text-blue-300 text-gray-300 mb-1 pl-2">
                            {{ fish.name }}
                        </li>
                    </ul>
                </div>
            </div>
            <div v-if="selectedFish" class="bg-black p-4 rounded-lg overflow-auto">
                <h3 class="text-xl font-semibold mb-2 text-white">Edit Fish: {{ selectedFish.name }}</h3>
                <div class="mb-4">
                    <label class="block mb-2 text-gray-300">Fish Name</label>
                    <input v-model="selectedFish.name" class="w-full bg-black text-white rounded p-2 border border-gray-700">
                </div>
                <div class="mb-4">
                    <label class="block mb-2 text-gray-300">Group</label>
                    <select v-model="selectedFish.group" @change="handleGroupChange('edit')" class="w-full bg-black text-white rounded p-2 border border-gray-700">
                        <option v-for="group in fishGroups" :key="group" :value="group">
                            {{ group }}
                        </option>
                        <option value="add_new_group">Add New Group</option>
                    </select>
                </div>
                <div v-if="showNewGroupInput.edit" class="mb-4">
                    <label class="block mb-2 text-gray-300">New Group Name</label>
                    <input v-model="newGroupName.edit" class="w-full bg-black text-white rounded p-2 border border-gray-700">
                </div>
                <div class="mb-4">
                    <label class="block mb-2 text-gray-300">Description</label>
                    <textarea v-model="selectedFish.description" rows="3" class="w-full bg-black text-white rounded p-2 border border-gray-700"></textarea>
                </div>
                <div class="flex justify-end space-x-2">
                    <button @click="deleteFish" class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded">
                        Delete
                    </button>
                    <div class="flex-grow"></div>
                    <button @click="cancelEdit" class="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded">
                        Cancel
                    </button>
                    <button @click="saveFish" class="bg-primary hover:bg-blue-600 text-white px-4 py-2 rounded">
                        Save
                    </button>
                </div>
            </div>
        </div>

        <!-- Add New Fish Modal -->
        <div v-if="showAddFishModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div class="bg-black p-6 rounded-lg w-96">
                <h3 class="text-xl font-semibold mb-4 text-white">Add New Fish</h3>
                <div class="mb-4">
                    <label class="block mb-2 text-gray-300">Fish Name</label>
                    <input v-model="newFish.name" class="w-full bg-black text-white rounded p-2 border border-gray-700">
                </div>
                <div class="mb-4">
                    <label class="block mb-2 text-gray-300">Group</label>
                    <select v-model="newFish.group" @change="handleGroupChange('add')" class="w-full bg-black text-white rounded p-2 border border-gray-700">
                        <option v-for="group in fishGroups" :key="group" :value="group">
                            {{ group }}
                        </option>
                        <option value="add_new_group">Add New Group</option>
                    </select>
                </div>
                <div v-if="showNewGroupInput.add" class="mb-4">
                    <label class="block mb-2 text-gray-300">New Group Name</label>
                    <input v-model="newGroupName.add" class="w-full bg-black text-white rounded p-2 border border-gray-700">
                </div>
                <div class="mb-4">
                    <label class="block mb-2 text-gray-300">Description</label>
                    <textarea v-model="newFish.description" rows="3" class="w-full bg-black text-white rounded p-2 border border-gray-700"></textarea>
                </div>
                <div class="flex justify-end">
                    <button @click="showAddFishModal = false" class="mr-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded">
                        Cancel
                    </button>
                    <button @click="addNewFish" class="bg-primary hover:bg-blue-600 text-white px-4 py-2 rounded">
                        Add Fish
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { collection, getDocs, doc, query, where, updateDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db_resto_new } from '../main.js';

export default {
    name: 'ManageFish',
    props: {
        branchId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            fishList: [],
            selectedFish: null,
            fishGroups: [],
            showAddFishModal: false,
            newFish: {
                name: '',
                group: '',
                description: ''
            },
            showNewGroupInput: {
                edit: false,
                add: false
            },
            newGroupName: {
                edit: '',
                add: ''
            }
        };
    },
    computed: {
        sortedFishList() {
            return [...this.fishList].sort((a, b) => a.name.localeCompare(b.name));
        },
        groupedFishList() {
            const grouped = {};
            this.fishList.forEach(fish => {
                if (!grouped[fish.ecological_role.function]) {
                    grouped[fish.ecological_role.function] = [];
                }
                grouped[fish.ecological_role.function].push(fish);
            });

            // Sort fish within each group
            Object.keys(grouped).forEach(group => {
                grouped[group].sort((a, b) => a.name.localeCompare(b.name));
            });

            // Create a new object with sorted keys (groups), "Other" at the end
            const sortedGrouped = {};
            Object.keys(grouped)
                .sort((a, b) => {
                    if (a.toLowerCase() === 'other') return 1;
                    if (b.toLowerCase() === 'other') return -1;
                    return a.localeCompare(b);
                })
                .forEach(key => {
                    sortedGrouped[key] = grouped[key];
                });

            return sortedGrouped;
        }
    },
    mounted() {
        this.fetchFishList();
    },
    methods: {
        async fetchFishList() {
            const fishRef = query(collection(db_resto_new, '_organisms'),
                    where('organism_type', '==', 'fish'));
            const querySnapshot = await getDocs(fishRef);
            this.fishList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            this.updateFishGroups(); // Update groups after fetching fish
        },
        updateFishGroups() {
            // Extract unique groups from the fish list
            this.fishGroups = [...new Set(this.fishList.map(fish => fish.ecological_role.function))].sort();
        },
        selectFish(fish) {
            this.selectedFish = { ...fish };
        },
        handleGroupChange(context) {
            if (context === 'edit' && this.selectedFish.ecological_role.function === 'add_new_group') {
                this.showNewGroupInput.edit = true;
            } else if (context === 'add' && this.newFish.ecological_role.function === 'add_new_group') {
                this.showNewGroupInput.add = true;
            } else {
                this.showNewGroupInput[context] = false;
                this.newGroupName[context] = '';
            }
        },
        async saveFish() {
            if (!this.selectedFish) return;

            if (this.selectedFish.ecological_role.function === 'add_new_group') {
                if (!this.newGroupName.edit) {
                    alert('Please enter a new group name');
                    return;
                }
                this.selectedFish.ecological_role.function = this.newGroupName.edit;
            }

            const fishDocRef = doc(db_resto_new, '_organisms', this.selectedFish.id);
            
            await updateDoc(fishDocRef, {
                name: this.selectedFish.name,
                ecological_role: {function: this.selectedFish.ecological_role.function},
                description: this.selectedFish.description
            });

            // Refresh the fish list data
            await this.fetchFishList();
            this.selectedFish = null;
            this.showNewGroupInput.edit = false;
            this.newGroupName.edit = '';
        },
        async addNewFish() {
            if (!this.newFish.name || !this.newFish.ecological_role.function) {
                alert('Please enter a fish name and select a group');
                return;
            }

            if (this.newFish.group === 'add_new_group') {
                if (!this.newGroupName.add) {
                    alert('Please enter a new group name');
                    return;
                }
                this.newFish.ecological_role.function = this.newGroupName.add;
            }

            const newFishRef = doc(db_resto_new, '_organisms');
            // Create new fish document with auto-generated ID
            await setDoc(newFishRef, {
                name: this.newFish.name,
                ecological_role: {function: this.newFish.ecological_role.function},
                description: this.newFish.description
            });

            // Refresh fish list data
            await this.fetchFishList();

            // Reset form and close modal
            this.newFish = { name: '', ecological_role: {function: ''}, description: '' };
            this.showAddFishModal = false;
            this.showNewGroupInput.add = false;
            this.newGroupName.add = '';
        },
        cancelEdit() {
            this.selectedFish = null;
            this.showNewGroupInput.edit = false;
            this.newGroupName.edit = '';
        },
        async deleteFish() {
            if (!this.selectedFish) return;

            if (confirm(`Are you sure you want to delete ${this.selectedFish.name}?`)) {
                const fishDocRef = doc(db_resto_new, '_organisms', this.selectedFish.id);
                
                try {
                    await deleteDoc(fishDocRef);
                    // Refresh the fish list data
                    await this.fetchFishList();
                    this.selectedFish = null;
                } catch (error) {
                    console.error("Error deleting fish:", error);
                    alert("An error occurred while deleting the fish. Please try again.");
                }
            }
        }
    }
};
</script>

<style scoped>
.grid {
    display: grid;
    gap: 1rem;
}

@media (min-width: 768px) {
    .grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

.no-scrollbar::-webkit-scrollbar {
    display: none;
}

.no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>